/**
 * useGlobalConversationList — shared owner of the global conversation list.
 *
 * Extracted from AgentV3View so the conversation list can live in the global
 * chrome (AppSidebar, always mounted) AND still be consumed by the assistant
 * page. It owns:
 *   - the global conversations query (TanStack, queryKey ['conversations','global'])
 *   - the phantom merge (optimistic rows from the Zustand store before a DB row exists)
 *   - the navigation actions (select / new / delete / rename)
 *
 * The ACTIVE conversation is NOT owned here — it lives in the URL (`?c=<id>`),
 * the single source of truth read by AgentV3View. Every action that opens or
 * switches a conversation navigates to `/assistant?c=<id>`, which is why this
 * hook works identically from the Dossiers page or anywhere else in the chrome.
 */

import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ErrorService } from "@/lib/errors";
import {
  useGlobalConversations,
  useDeleteConversation,
  useUpdateConversation,
} from "@/hooks/useConversationsAPI";
import { useAuth } from "@/components/AuthProvider";
import type { Conversation } from "@/types/chat";
import type { ConversationArchiveFilter } from "@hector/shared-types";
import { useActiveSessionsStore } from "../stores/activeSessionsStore";
import { publierConversationAffichee } from "../state/conversationAffichee";
import { WebSocketPool } from "../services/WebSocketPool";
import {
  deciderDeLaSuppression,
  libererLesRessourcesDuFil,
  lireLaProtectionDuFil,
} from "@/features/redaction/state/suppressionDeConversation";
import {
  leverLaSuppressionEnCours,
  marquerLaSuppressionEnCours,
} from "@/features/redaction/state/filsEnSuppression";

/**
 * Sépare les conversations « miennes » (je suis owner) des « partagées avec moi »
 * (un autre membre m'a accordé un accès `viewer`/`editor`).
 *
 * Fonction pure (hors React) pour être testée seule. Règle : un fil est *partagé
 * avec moi* SSI `my_role ∈ {viewer, editor}` ET `ownerId !== currentUserId`. Les
 * phantoms optimistes (pas encore de row DB, donc `my_role` absent) et tout fil
 * dont je suis l'owner tombent dans `owned`. Sans `currentUserId` connu (auth pas
 * encore prête), on considère tout comme `owned` — pas de fausse section vide.
 */
export function splitConversationsByOwnership(
  conversations: Conversation[],
  currentUserId: string | null | undefined,
): { owned: Conversation[]; shared: Conversation[] } {
  const owned: Conversation[] = [];
  const shared: Conversation[] = [];
  for (const c of conversations) {
    const isSharedWithMe =
      !!currentUserId &&
      (c.my_role === "viewer" || c.my_role === "editor") &&
      c.ownerId != null &&
      c.ownerId !== currentUserId;
    (isSharedWithMe ? shared : owned).push(c);
  }
  return { owned, shared };
}

export interface UseGlobalConversationListResult {
  /** DB conversations merged with optimistic phantoms (FULL list — for lookups). */
  conversations: Conversation[];
  /** Sous-ensemble dont je suis l'owner — liste principale de la sidebar. */
  ownedConversations: Conversation[];
  /** Sous-ensemble partagé avec moi (`viewer`/`editor`) — section dédiée. */
  sharedConversations: Conversation[];
  isLoading: boolean;
  /** Active conversation id read from the URL (`?c=`), or null. */
  activeConversationId: string | null;
  /** Navigate to a conversation (works from any page). */
  selectConversation: (conversationId: string) => void;
  /** Create a fresh phantom conversation and navigate to it. */
  startNewConversation: () => void;
  /** Delete a conversation; if it was the active one, leave it (clean URL). */
  deleteConversation: (conversationId: string) => Promise<void>;
  /** Rename a conversation (optimistic update lives in the mutation). */
  renameConversation: (conversationId: string, newTitle: string) => Promise<void>;
}

export function useGlobalConversationList(
  options: { archived?: ConversationArchiveFilter } = {},
): UseGlobalConversationListResult {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeConversationId = searchParams.get("c");
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  const { data: dbConversations = [], isLoading } = useGlobalConversations(options);
  const { mutateAsync: deleteMutation } = useDeleteConversation();
  const { mutateAsync: updateMutation } = useUpdateConversation();

  const pendingConversations = useActiveSessionsStore((s) => s.pendingConversations);

  const conversations = useMemo<Conversation[]>(() => {
    // WHY phantom merge: new conversations don't have a DB row until the first
    // global-list refetch observes the server-side INSERT.
    // Phantoms provide immediate sidebar feedback. Auto-dedup: once the DB query
    // returns the real row (same UUID), the phantom is filtered out by the dbIds
    // check. The sidebar handles its own source/date/search filtering downstream.
    const dbIds = new Set(dbConversations.map((c) => c.id));
    const phantoms: Conversation[] = Object.values(pendingConversations)
      .filter((p) => !dbIds.has(p.id))
      .map((p) => ({
        id: p.id,
        caseId: p.caseId,
        caseTitle: null,
        title: "Nouvelle conversation",
        summary: undefined,
        messageCount: 0,
        totalTokensUsed: 0,
        isArchived: false,
        isPinned: false,
        tags: [],
        createdAt: new Date(p.createdAt),
        updatedAt: new Date(p.createdAt),
        lastMessageAt: new Date(p.createdAt),
        sourceSystem: "hector_case_discovery_v3",
      }));

    return [...phantoms, ...dbConversations];
  }, [dbConversations, pendingConversations]);

  useEffect(() => {
    const dbConversationIds = new Set(dbConversations.map((conversation) => conversation.id));
    const store = useActiveSessionsStore.getState();

    // A WebSocket confirmation proves persistence, but it does not populate the
    // TanStack cache. Removing the phantom there created a visible gap while the
    // global HTTP list was still in flight. This effect runs only after the API
    // row is present, and the memo above already deduplicates that render by UUID.
    for (const pendingConversationId of Object.keys(pendingConversations)) {
      if (dbConversationIds.has(pendingConversationId)) {
        store.removePendingConversation(pendingConversationId);
      }
    }
  }, [dbConversations, pendingConversations]);

  const { ownedConversations, sharedConversations } = useMemo(() => {
    const { owned, shared } = splitConversationsByOwnership(conversations, currentUserId);
    return { ownedConversations: owned, sharedConversations: shared };
  }, [conversations, currentUserId]);

  const selectConversation = useCallback(
    (conversationId: string) => {
      navigate(`/assistant?c=${conversationId}`);
      // The URL remains the persistent identity, but publish the human gesture
      // immediately so the assistant cannot keep rendering the previous stream
      // in the render gap before Router notifies its subscribers.
      publierConversationAffichee(conversationId);
    },
    [navigate],
  );

  const startNewConversation = useCallback(() => {
    // No conversation is created until the first message is sent: just land on
    // the empty assistant (clean URL, no ?c=, no phantom). V3ChatInterface mints
    // the conversation id + phantom on send, which is when it appears in the list
    // and the URL becomes /assistant?c=<id>.
    navigate("/assistant");
    publierConversationAffichee(null);
  }, [navigate]);

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      // A conversation still in the phantom store may be awaiting its first
      // global-list reconciliation. A network DELETE would 404 and leave the
      // optimistic row stuck — just drop the phantom locally instead.
      if (useActiveSessionsStore.getState().pendingConversations[conversationId]) {
        useActiveSessionsStore.getState().removePendingConversation(conversationId);
        if (conversationId === activeConversationId) {
          navigate("/assistant", { replace: true });
        }
        return;
      }
      // LA GARDE LOCALE DE SUPPRESSION (#1334). Avant elle, ce parcours était
      // un DELETE et une invalidation de cache : la socket du fil restait
      // ouverte et reconnectable, son document logique gardait une des quatre
      // places de résidence avec son contrôleur de brouillon et ses PUT en vol,
      // et les callbacks de montage désignaient encore une session dont la
      // conversation n'existait plus en base.
      const decision = deciderDeLaSuppression(lireLaProtectionDuFil(conversationId));
      switch (decision.geste) {
        case "deja-en-cours":
          // Un second clic pendant le trajet réseau : ne rien refaire, et ne
          // surtout pas ré-émettre un DELETE qui rendrait 404.
          return;
        case "attendre-la-fin-du-travail":
          // Aucun travail n'est DÉTACHÉ : on demande l'arrêt, on ne supprime pas
          // sous une rédaction en cours.
          toast.error(decision.raison);
          return;
        case "supprimer":
          break;
      }
      // Marqué AVANT le réseau : un tour lancé pendant le trajet ressusciterait
      // sinon le document qu'on s'apprête à retirer.
      marquerLaSuppressionEnCours(conversationId);
      try {
        await deleteMutation(conversationId);
      } catch (error) {
        // ÉCHEC RÉSEAU : fil et document conservés tels quels, et la fenêtre est
        // levée pour que l'avocat puisse réessayer — sans quoi son fil resterait
        // muet pour le reste de l'onglet.
        leverLaSuppressionEnCours(conversationId);
        ErrorService.handle(error, { context: { action: "delete_conversation" } });
        toast.error("Impossible de supprimer la conversation");
        return;
      }
      // DELETE CONFIRMÉ, et seulement là : connexion et reprises fermées,
      // document logique et montage retirés, callbacks invalidés. Le marquage
      // n'est PAS levé — le fil n'existe plus, et le garder marqué interdit
      // toute résurrection tardive par une trame en retard.
      //
      // ⚠️ **HORS du `try` du réseau**, et ce n'est pas cosmétique (revue #1334) :
      // cette libération démonte SuperDoc et émet `session_removed` vers des
      // abonnés. Une exception venue de là, capturée par le `catch` ci-dessus,
      // aurait levé la fenêtre sur un fil DÉJÀ détruit en base — rouvrant la
      // porte à une résurrection tardive —, affiché « Impossible de supprimer »
      // à tort, et laissé l'avocat sur un `?c=` mort.
      try {
        libererLesRessourcesDuFil(conversationId, {
          fermerLaConnexion: (id) => { WebSocketPool.getInstance().closeConversation(id); },
        });
      } catch (error) {
        // La conversation EST supprimée : ne rien dire à l'avocat, tracer pour
        // le prochain lecteur. Le marquage reste posé, donc rien ne peut
        // ressusciter le document même si son retrait a échoué à mi-chemin.
        console.error(
          `[canvas] libération incomplète après la suppression de conversationId=${conversationId} : `
          + `${error instanceof Error ? error.message : String(error)}. La conversation est bien `
          + "supprimée côté serveur ; son document local peut occuper une place de résidence "
          + "jusqu'au prochain rechargement de l'onglet.",
        );
      }
      // If we just deleted the conversation currently open, drop back to the
      // empty assistant so the chat doesn't 404 on a dead id.
      if (conversationId === activeConversationId) {
        navigate("/assistant", { replace: true });
      }
    },
    [deleteMutation, activeConversationId, navigate],
  );

  const renameConversation = useCallback(
    async (conversationId: string, newTitle: string) => {
      try {
        await updateMutation({
          conversationId,
          data: { conversation_title: newTitle },
        });
      } catch (error) {
        ErrorService.handle(error, { context: { action: "rename_conversation" } });
        toast.error("Impossible de renommer la conversation");
        throw error;
      }
    },
    [updateMutation],
  );

  return {
    conversations,
    ownedConversations,
    sharedConversations,
    isLoading,
    activeConversationId,
    selectConversation,
    startNewConversation,
    deleteConversation,
    renameConversation,
  };
}
